/**
 * Static English typing exercises.
 * Each entry is self-contained — no backend required.
 * Structure mirrors Snippet so it can slot into the same typing pipeline.
 */

export type ExerciseDifficulty = 'beginner' | 'intermediate' | 'advanced';

export interface EnglishExercise {
  id: string;
  title: string;
  difficulty: ExerciseDifficulty;
  content: string;
  charCount: number;
}

const raw: Omit<EnglishExercise, 'charCount'>[] = [
  // ── Beginner ───────────────────────────────────────────────────────────────
  {
    id: 'en-b-01',
    title: 'The Quick Brown Fox',
    difficulty: 'beginner',
    content: 'The quick brown fox jumps over the lazy dog.',
  },
  {
    id: 'en-b-02',
    title: 'Home Row Practice',
    difficulty: 'beginner',
    content: 'All sad lads ask dad. A flask falls flat. Dad adds a salad.',
  },
  {
    id: 'en-b-03',
    title: 'Common Words',
    difficulty: 'beginner',
    content: 'the and for are but not you all can had her was one our out day get has him his how man new now old see two way who boy did its let put say she too use',
  },
  {
    id: 'en-b-04',
    title: 'Simple Sentence',
    difficulty: 'beginner',
    content: 'She sells seashells by the seashore. The shells she sells are seashells.',
  },
  {
    id: 'en-b-05',
    title: 'Numbers and Letters',
    difficulty: 'beginner',
    content: 'Pack my box with five dozen liquor jugs.',
  },

  // ── Intermediate ───────────────────────────────────────────────────────────
  {
    id: 'en-i-01',
    title: 'Technology',
    difficulty: 'intermediate',
    content: 'The internet has transformed how people communicate, learn, and access information. Digital tools have made collaboration across continents seamless and instantaneous.',
  },
  {
    id: 'en-i-02',
    title: 'Science',
    difficulty: 'intermediate',
    content: 'The scientific method involves observation, hypothesis formation, experimentation, and conclusion. Reproducibility is essential to validate any scientific claim.',
  },
  {
    id: 'en-i-03',
    title: 'Punctuation Practice',
    difficulty: 'intermediate',
    content: 'Wait — are you sure? Yes, I\'m certain: the meeting starts at 9:00 a.m., not 9:30. Don\'t be late; punctuality matters.',
  },
  {
    id: 'en-i-04',
    title: 'Business Writing',
    difficulty: 'intermediate',
    content: 'Please review the attached proposal and provide feedback by Friday. We need to align on the project scope before the client presentation next week.',
  },
  {
    id: 'en-i-05',
    title: 'Nature',
    difficulty: 'intermediate',
    content: 'Forests cover roughly thirty percent of the Earth\'s land surface. They absorb carbon dioxide, produce oxygen, and provide habitat for countless species.',
  },

  // ── Advanced ───────────────────────────────────────────────────────────────
  {
    id: 'en-a-01',
    title: 'Philosophy',
    difficulty: 'advanced',
    content: 'Epistemology, the branch of philosophy concerned with knowledge, asks fundamental questions: What can we know? How do we distinguish justified belief from mere opinion? Can certainty ever be achieved?',
  },
  {
    id: 'en-a-02',
    title: 'Technical Writing',
    difficulty: 'advanced',
    content: 'Distributed systems must handle network partitions, latency variance, and partial failures gracefully. The CAP theorem states that consistency, availability, and partition tolerance cannot all be guaranteed simultaneously.',
  },
  {
    id: 'en-a-03',
    title: 'Literature',
    difficulty: 'advanced',
    content: 'It was the best of times, it was the worst of times, it was the age of wisdom, it was the age of foolishness, it was the epoch of belief, it was the epoch of incredulity.',
  },
  {
    id: 'en-a-04',
    title: 'Mixed Punctuation',
    difficulty: 'advanced',
    content: 'The algorithm\'s time complexity is O(n log n) — significantly better than O(n²). However, the constant factor matters: a well-tuned O(n²) implementation often outperforms a naive O(n log n) one for small n.',
  },
  {
    id: 'en-a-05',
    title: 'Long Form',
    difficulty: 'advanced',
    content: 'Effective communication requires clarity of thought, precision of language, and sensitivity to audience. A message that is technically accurate but poorly structured will fail to persuade, inform, or inspire.',
  },
];

export const ENGLISH_EXERCISES: EnglishExercise[] = raw.map((e) => ({
  ...e,
  charCount: e.content.length,
}));
